import React, { ChangeEvent, useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from "@material-ui/core";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import DeleteIcon from "@material-ui/icons/Delete";
import { ValidatorForm, TextValidator } from "react-material-ui-form-validator";
import { useSnackbar } from "notistack";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { IDocumento } from "../../interfaces/IDocumento";
import { IEvent } from "../../interfaces/IEvent";
import NumberField from "../controls/NumberField";
import ConfirmDialog from "../controls/ConfirmDialog";
import { traducirButton, traducirLabel, traducirMessage } from "../../blocks/traductor";
import {
    clonarDocumentosCliente,
    construirFilasDocumentosPorCantidad,
    documentosInicialesRetorno,
    MAX_DOCUMENTOS_RETORNO
} from "./retornoDocumentosGuiaHelpers";

interface Props {
    open: boolean;
    servicio: ServicioAdicional;
    documentosCliente: IDocumento[];
    onGuardar: (servicio: ServicioAdicional) => void;
    onCerrar: () => void;
}

const RetornoDocumentosDialogGuia = ({
    open,
    servicio,
    documentosCliente,
    onGuardar,
    onCerrar
}: Props) => {
    const { enqueueSnackbar } = useSnackbar();

    const [servicioDialog, setServicioDialog] = useState<ServicioAdicional>(new ServicioAdicional(servicio));
    const [documentos, setDocumentos] = useState<IDocumento[]>([]);
    const [cambioEnDocumentos, setCambioEnDocumentos] = useState(false);
    const [mostrarConfirm, setMostrarConfirm] = useState(false);
    const [documentosInicio, setDocumentosInicio] = useState<IDocumento[]>([]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const documentosClienteClon = clonarDocumentosCliente(documentosCliente);
        const inicio = documentosInicialesRetorno(servicio, documentosClienteClon);
        setServicioDialog(new ServicioAdicional(servicio));
        setDocumentos(inicio);
        setDocumentosInicio(inicio);
        setCambioEnDocumentos(false);
        setMostrarConfirm(false);
    }, [open, servicio, documentosCliente]);

    const totalDocumentosCliente = documentosCliente.length;

    const tituloDialog = localStorage.getItem("idioma") === "en-US"
        ? (servicio.etiquetaServicioAdicionalDto?.nombre || servicio.nombre)
        : servicio.nombre;

    const handleCantidad = (event: ChangeEvent<HTMLInputElement> | IEvent) => {
        let value = (event.target.value || event.target.value === 0)
            ? Number(event.target.value)
            : null;
        if (value != null) {
            value = totalDocumentosCliente > Number(value) ? totalDocumentosCliente : value;
            if (value > MAX_DOCUMENTOS_RETORNO) {
                value = MAX_DOCUMENTOS_RETORNO;
            }
        }
        setServicioDialog(new ServicioAdicional({ ...servicioDialog, cantidad: value }));
    };

    const agregarRetornos = (event: ChangeEvent<HTMLInputElement> | IEvent) => {
        if (!event.target.value) {
            return;
        }
        let value = Number(event.target.value);
        value = totalDocumentosCliente > value ? totalDocumentosCliente : value;
        if (value > MAX_DOCUMENTOS_RETORNO) {
            value = MAX_DOCUMENTOS_RETORNO;
        }
        setServicioDialog(new ServicioAdicional({ ...servicioDialog, cantidad: value }));
        setDocumentos(construirFilasDocumentosPorCantidad(value, documentos, clonarDocumentosCliente(documentosCliente)));
    };

    const addDocumento = () => {
        const nuevoDocumento: IDocumento = {
            id: 0,
            clienteDocumentoID: 0,
            documento: "",
            descripcion: traducirLabel(4)
        };
        nuevoDocumento.id = (documentos.length === 0 ? 1 : Math.max(...documentos.map(d => d.id))) + 1;
        const nuevos = [...documentos, nuevoDocumento];
        setDocumentos(nuevos);
        setServicioDialog(new ServicioAdicional({ ...servicioDialog, cantidad: nuevos.length }));
        setCambioEnDocumentos(true);
    };

    const deleteDocumento = (id: number) => {
        const copiaDocumentos = documentos.filter(m => m.id !== id);
        setDocumentos(copiaDocumentos);
        setServicioDialog(new ServicioAdicional({ ...servicioDialog, cantidad: copiaDocumentos.length }));
        setCambioEnDocumentos(true);
    };

    const updateDocumento = (event: ChangeEvent<HTMLInputElement> | IEvent, documento: IDocumento) => {
        const name = event.target.name || "";
        const value = String(event.target.value ?? "");
        const copiaDocumento = { ...documento, [name]: value };
        setDocumentos(documentos.map(d => d.id !== documento.id ? d : copiaDocumento));
        setCambioEnDocumentos(true);
    };

    const cerrarSinGuardar = () => {
        const totalDocumentos = documentosInicio.length;
        if ((cambioEnDocumentos || documentos.length !== totalDocumentos) && !mostrarConfirm) {
            setMostrarConfirm(true);
            return;
        }
        setMostrarConfirm(false);
        onCerrar();
    };

    const seleccionarDesdeDialog = () => {
        const copiaDocumentos = documentos.map(d => ({ ...d }));
        const copiaServicio = new ServicioAdicional({
            ...servicioDialog,
            documentos: copiaDocumentos,
            esRetornoDocumentos: true
        });
        if (Number(copiaServicio.cantidad) > 0) {
            copiaServicio.Seleccionar(true, false);
        }
        if (!copiaServicio.esFormaValida) {
            enqueueSnackbar(traducirMessage(131), { variant: "error" });
            return;
        }
        onGuardar(copiaServicio);
    };

    return (
        <>
            <Dialog open={open} maxWidth="sm" fullWidth={true} onClose={cerrarSinGuardar}>
                <DialogTitle id="retorno-documentos-guia-title">{tituloDialog}</DialogTitle>
                <ValidatorForm onSubmit={seleccionarDesdeDialog}>
                    <DialogContent dividers>
                        <Box display="flex" mb={2}>
                            <Box mr={4}>
                                <TextValidator
                                    label={traducirLabel(29)}
                                    value={servicioDialog.cantidad ?? ""}
                                    name="cantidad"
                                    onChange={handleCantidad}
                                    onBlur={agregarRetornos}
                                    validators={["required"]}
                                    errorMessages={[traducirLabel(126)]}
                                    disabled={true}
                                    InputProps={{
                                        inputComponent: NumberField as any,
                                        inputProps: { precision: 0, maxLength: 2 },
                                    }}
                                />
                            </Box>
                        </Box>
                        <TableContainer>
                            <Table size="small" aria-label="retorno-documentos-guia">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center" style={{ width: "45%" }}>
                                            {traducirLabel(102)}
                                        </TableCell>
                                        <TableCell align="center" style={{ width: "45%" }}>
                                            {traducirLabel(81)}
                                        </TableCell>
                                        <TableCell align="center" style={{ padding: 0, width: "10%" }}>
                                            <AddCircleIcon
                                                color="primary"
                                                style={{ fontSize: "1rem" }}
                                                className="pointer"
                                                onClick={addDocumento}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {documentos.map(row => (
                                        <TableRow key={row.id}>
                                            <TableCell>
                                                <TextValidator
                                                    value={row.documento}
                                                    name="documento"
                                                    onChange={(e) => updateDocumento(e, row)}
                                                    validators={["required"]}
                                                    errorMessages={[""]}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    value={row.descripcion}
                                                    fullWidth
                                                    disabled
                                                    name="descripcion"
                                                />
                                            </TableCell>
                                            <TableCell style={{ padding: "0 5px 0 0" }}>
                                                {row.clienteDocumentoID === 0 && (
                                                    <DeleteIcon
                                                        color="error"
                                                        style={{ fontSize: "1rem" }}
                                                        className="pointer"
                                                        onClick={() => deleteDocumento(row.id)}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="contained" color="primary">
                            {traducirButton(12)}
                        </Button>
                        <Button onClick={cerrarSinGuardar} variant="contained">
                            {traducirButton(16)}
                        </Button>
                    </DialogActions>
                </ValidatorForm>
            </Dialog>
            {mostrarConfirm && (
                <ConfirmDialog
                    titulo={traducirLabel(32)}
                    contenido={traducirMessage(165)}
                    abierto={mostrarConfirm}
                    confirmar={() => {
                        setMostrarConfirm(false);
                        onCerrar();
                    }}
                    cerrar={() => setMostrarConfirm(false)}
                />
            )}
        </>
    );
};

export default RetornoDocumentosDialogGuia;
